import maplibregl from 'maplibre-gl';
import { SatelliteFrame } from '../services/LiveWeatherService';

export interface SatelliteFrameInfo {
  tileUrl: string;
  formattedTime: string;
  time: number;
}

export class CanvasSatelliteRenderer {
  private map: maplibregl.Map;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private imageCache: Map<string, HTMLImageElement> = new Map();
  private failedUrls: Set<string> = new Set();
  
  private currentFrameUrl: string | null = null;
  private currentOpacity: number = 0.8;
  private active: boolean = false;
  private lastDrawnImage: HTMLImageElement | null = null;
  private currentBlendUrls: Record<string, string> | null = null;

  private onMapMoveBound: () => void;

  private SATELLITE_EXTENTS: Record<string, { west: number; south: number; east: number; north: number }> = {
    goes_composite: { west: -175.0, south: -75.0, east: -15.0, north: 75.0 },
    goes_west: { west: -175.0, south: -75.0, east: -75.0, north: 75.0 },
    goes_east: { west: -105.0, south: -75.0, east: -15.0, north: 75.0 },
    himawari: { west: 80.0, south: -75.0, east: 200.0, north: 75.0 },
    meteosat: { west: -75.0, south: -75.0, east: 75.0, north: 75.0 },
    gmgsi: { west: -180.0, south: -72.74, east: 180.0, north: 72.72 },
    global_ir: { west: -180.0, south: -70.0, east: 180.0, north: 70.0 },
    goes_conus: { west: -175.0, south: -75.0, east: -15.0, north: 75.0 }
  };

  private extent = {
    west: -175.0,
    south: -75.0,
    east: -15.0,
    north: 75.0
  };

  constructor(map: maplibregl.Map) {
    this.map = map;
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'live-satellite-canvas';
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '1';

    const container = map.getContainer();
    container.appendChild(this.canvas);

    this.ctx = this.canvas.getContext('2d')!;
    this.onMapMoveBound = () => this.redraw();

    this.resizeCanvas();
    this.map.on('move', this.onMapMoveBound);
    this.map.on('moveend', this.onMapMoveBound);
    this.map.on('resize', () => {
      this.resizeCanvas();
      this.redraw();
    });
  }

  private resizeCanvas(): void {
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = container.clientWidth * dpr;
    this.canvas.height = container.clientHeight * dpr;
    this.canvas.style.width = `${container.clientWidth}px`;
    this.canvas.style.height = `${container.clientHeight}px`;
    
    this.ctx.resetTransform();
    this.ctx.scale(dpr, dpr);
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
  }

  public update(
    frameUrl: string | null,
    opacity: number,
    active: boolean,
    source?: string,
    blendUrls?: Record<string, string>
  ): void {
    this.currentFrameUrl = frameUrl;
    this.currentOpacity = opacity;
    this.active = active;
    if (source) {
      if (this.SATELLITE_EXTENTS[source]) {
        this.extent = this.SATELLITE_EXTENTS[source];
      }
    }
    this.currentBlendUrls = blendUrls || null;
    this.failedUrls.clear();
    this.redraw();
  }

  public preloadSequence(frames: Array<SatelliteFrame>): void {
    if (!frames || frames.length === 0) return;
    frames.forEach(f => {
      if (f.blendUrls) {
        Object.values(f.blendUrls).forEach(url => {
          if (url) this.loadImage(url);
        });
      } else if (f.tileUrl) {
        const webpUrl = f.tileUrl.replace(/\.pmtiles$/, '.webp').replace('pmtiles://', '');
        this.loadImage(webpUrl);
      }
    });
  }

  /**
   * Preloads and waits for the first N frames to load into imageCache before playback starts.
   */
  public async waitForFrames(frames: Array<SatelliteFrame>, count: number = 8, timeoutMs: number = 1200): Promise<void> {
    if (!frames || frames.length === 0) return;
    this.preloadSequence(frames);

    const targetFrames = frames.slice(0, Math.min(frames.length, count));
    const urlsToWait: string[] = [];
    targetFrames.forEach(f => {
      if (f.blendUrls) {
        Object.values(f.blendUrls).forEach(url => {
          if (url) urlsToWait.push(url);
        });
      } else if (f.tileUrl) {
        const webpUrl = f.tileUrl.replace(/\.pmtiles$/, '.webp').replace('pmtiles://', '');
        urlsToWait.push(webpUrl);
      }
    });

    const promises = urlsToWait.map(url => {
      const existing = this.imageCache.get(url);
      if (existing && existing.complete && existing.naturalWidth !== 0) {
        return Promise.resolve();
      }
      return new Promise<void>(resolve => {
        const img = new Image();
        img.crossOrigin = 'use-credentials';
        img.onload = () => {
          this.imageCache.set(url, img);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = url;
      });
    });

    await Promise.race([
      Promise.all(promises),
      new Promise(resolve => setTimeout(resolve, timeoutMs))
    ]);
  }

  private loadImage(url: string): HTMLImageElement | null {
    if (this.imageCache.has(url)) {
      return this.imageCache.get(url)!;
    }
    if (this.failedUrls.has(url)) {
      return null;
    }

    const img = new Image();
    img.crossOrigin = 'use-credentials';
    img.onload = () => {
      this.imageCache.set(url, img);
      const isCurrentBlend = this.currentBlendUrls && Object.values(this.currentBlendUrls).some(u => u === url);
      const isCurrentFrame = this.currentFrameUrl && (this.currentFrameUrl.includes(url) || url.includes(this.currentFrameUrl.replace('pmtiles://', '')));
      if (isCurrentFrame || isCurrentBlend) {
        this.redraw();
      }
    };
    img.onerror = () => {
      this.failedUrls.add(url);
    };
    img.src = url;
    return null;
  }

  private drawImageLayer(img: HTMLImageElement, extent: { west: number; south: number; east: number; north: number }): void {
    const SLICES = 96;
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;
    const latSpan = extent.north - extent.south;
    const container = this.map.getContainer();
    const mapW = container.clientWidth;

    for (const lonOffset of [0, -360, 360]) {
      const west = extent.west + lonOffset;
      const east = extent.east + lonOffset;

      const testNW = this.map.project([west, extent.north]);
      const testSE = this.map.project([east, extent.south]);
      if (testSE.x < -100 || testNW.x > mapW + 100) {
        continue;
      }

      for (let i = 0; i < SLICES; i++) {
        const frac1 = i / SLICES;
        const frac2 = (i + 1) / SLICES;

        const lat1 = extent.north - frac1 * latSpan;
        const lat2 = extent.north - frac2 * latSpan;

        const srcY1 = Math.floor(frac1 * imgH);
        const srcY2 = Math.ceil(frac2 * imgH);
        const srcH = Math.max(1, srcY2 - srcY1);

        const nw = this.map.project([west, lat1]);
        const se = this.map.project([east, lat2]);

        const destX = nw.x;
        const destY = nw.y;
        const destW = se.x - nw.x;
        const destH = Math.max(0.5, se.y - nw.y);

        this.ctx.drawImage(
          img,
          0, srcY1, imgW, srcH,
          destX, destY, destW, destH + 0.5
        );
      }
    }
  }

  private DRAW_ORDER: string[] = ['gmgsi', 'himawari', 'meteosat', 'global_ir', 'goes_west', 'goes_east', 'goes_composite'];

  public redraw(): void {
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    this.ctx.clearRect(0, 0, container.clientWidth * dpr, container.clientHeight * dpr);

    if (!this.active) {
      return;
    }

    this.ctx.globalAlpha = this.currentOpacity;

    if (this.currentBlendUrls) {
      // Draw satellite layers in order: Himawari -> Meteosat -> Global IR -> GOES-West -> GOES-East (Top)
      for (const src of this.DRAW_ORDER) {
        const url = this.currentBlendUrls[src];
        if (!url) continue;

        const extent = this.SATELLITE_EXTENTS[src];
        if (!extent) continue;

        let img = this.imageCache.get(url);
        if (!img) {
          img = this.loadImage(url) || undefined;
        }
        if (img) {
          this.drawImageLayer(img, extent);
        }
      }
    } else if (this.currentFrameUrl) {
      const webpUrl = this.currentFrameUrl.replace(/\.pmtiles$/, '.webp').replace('pmtiles://', '');
      let img: HTMLImageElement | null | undefined = this.imageCache.get(webpUrl);
      if (!img) {
        img = this.loadImage(webpUrl);
      }

      if (img) {
        this.lastDrawnImage = img;
      } else if (this.lastDrawnImage) {
        img = this.lastDrawnImage;
      } else {
        return;
      }

      this.drawImageLayer(img, this.extent);
    }

    this.ctx.globalAlpha = 1.0;
  }

  public clearCache(): void {
    this.imageCache.clear();
    this.failedUrls.clear();
    this.lastDrawnImage = null;
  }

  public clear(): void {
    this.active = false;
    this.lastDrawnImage = null;
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    this.ctx.clearRect(0, 0, container.clientWidth * dpr, container.clientHeight * dpr);
  }

  public destroy(): void {
    this.clear();
    this.map.off('move', this.onMapMoveBound);
    this.map.off('moveend', this.onMapMoveBound);
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

export default CanvasSatelliteRenderer;
